import { useState } from "react";

import { HeatingNetworkType, UserRole, type Building } from "@eisox/api/core-api";
import { Alert, Button, CanAccess, Drawer, Field } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { PencilIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { z } from "@eisox/zod";
import { useSelector } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { gatewayApi, heatingNetworkApi, valveApi } from "~/api";
import { DeleteDialog } from "~/components/delete-dialog";
import { VersionGate } from "~/components/version-gate";
import {
  TEMPERATURE_CORRECTION_COEF_LIMITS_MAX,
  TEMPERATURE_CORRECTION_COEF_LIMITS_MIN,
  TEMPERATURE_CORRECTION_COEF_LIMITS_STEP,
} from "~/constants/valve.constant";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import type { Permission } from "~/types/auth.type";
import type { EnhancedValve } from "~/types/valve.type";

interface EditValveDrawerProps extends Drawer.DrawerProps {
  valve: EnhancedValve;
  building: Building;
  permissions: Permission;
}

export const EditValveDrawer: React.FC<EditValveDrawerProps> = ({
  valve,
  building,
  permissions,
  children,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const { buildingId } = useParams({ strict: false });
  const { role } = useBuildingContext("EditValveDrawer");
  const queryClient = useQueryClient();

  const { data: gateways = [] } = useQuery({ ...gatewayApi.queries.building(buildingId ?? ""), enabled: !!buildingId });
  const { data: heatingNetworks = [] } = useQuery({
    ...heatingNetworkApi.queries.building(buildingId ?? ""),
    enabled: !!buildingId,
  });

  const gatewayOptions = gateways.map(g => ({ value: g.id, name: `${g.uid} - ${g.name}` }));
  const heatingNetworkOptions = heatingNetworks
    .filter(h => h.type === HeatingNetworkType.RADIATOR)
    .map(h => ({ value: h.id, name: h.name }));
  const showHeatingNetwork =
    !!permissions.valve?.heatingNetworkId?.read && building.pages.boilerRooms && heatingNetworkOptions.length > 0;

  const form = useAppForm({
    defaultValues: {
      uid: valve.uid,
      gatewayId: valve.gatewayId,
      heatingNetworkId: valve.heatingNetworkId ?? "",
      temperatureCorrectionCoef: valve.commands?.temperatureCorrectionCoef,
    },
    validators: {
      onSubmit: z.object({
        uid: z.number().positive(),
        gatewayId: z.string().nonempty(),
        heatingNetworkId: z.string(),
        temperatureCorrectionCoef: z
          .number()
          .min(TEMPERATURE_CORRECTION_COEF_LIMITS_MIN)
          .max(TEMPERATURE_CORRECTION_COEF_LIMITS_MAX)
          .or(z.undefined()),
      }),
    },
    onSubmit: async ({ value, formApi }) => {
      if (!buildingId) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const { heatingNetworkId, ...rest } = dirty;
      await valveApi.update(buildingId, [
        {
          ids: [valve.id],
          ...rest,
          ...(showHeatingNetwork && heatingNetworkId && { heatingNetworkId }),
        },
      ]);
      await queryClient.invalidateQueries({ queryKey: valveApi.queries.building(buildingId).queryKey });
      setOpen(false);
    },
  });

  const temperatureCorrectionCoef = useSelector(form.store, state => state.values.temperatureCorrectionCoef);

  const handleOpenChange = (open: boolean) => {
    if (open) form.reset();
    setOpen(open);
  };

  const handleDeleteValve = async () => {
    if (!buildingId) return;
    await valveApi.delete(buildingId, valve.id);
    await queryClient.invalidateQueries({ queryKey: valveApi.queries.building(buildingId).queryKey });
    setOpen(false);
  };

  // Command and device-reported echo diverge until the box next syncs.
  const calibrationOutOfSync = valve.commands?.temperatureCorrectionCoef !== valve.stateData?.temperatureCorrectionCoef;

  return (
    <Drawer.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Drawer.Trigger asChild>{children}</Drawer.Trigger>
      <Drawer.Content>
        <Drawer.Header>{m.modify_valve()}</Drawer.Header>
        <form
          className="min-h-0 md:gap-[20px] md:p-[40px] flex flex-1 flex-col gap-[10px] p-[10px]"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="flex flex-1 flex-col gap-[20px] overflow-y-auto">
              <Field.Root>
                <Field.Label>{m.mac()}</Field.Label>
                {valve.mac ?? "-"}
              </Field.Root>

              <CanAccess isAuthorized={permissions.valve?.uid?.update}>
                <form.AppField name="uid">
                  {field => <field.NumberField classNames={{ control: "w-full" }} label={m.number()} />}
                </form.AppField>
              </CanAccess>

              <CanAccess isAuthorized={permissions.valve?.gatewayId?.update}>
                <form.AppField name="gatewayId">
                  {field => (
                    <field.Select
                      classNames={{ control: "w-full" }}
                      label={m.related_box()}
                      placeholder={m.select_gateway()}
                      options={gatewayOptions}
                      multiple={false}
                    />
                  )}
                </form.AppField>
              </CanAccess>

              {showHeatingNetwork && (
                <form.AppField name="heatingNetworkId">
                  {field => (
                    <field.Select
                      classNames={{ control: "w-full" }}
                      label={m.heating_network()}
                      placeholder={m.select_heating_network()}
                      options={heatingNetworkOptions}
                      multiple={false}
                    />
                  )}
                </form.AppField>
              )}

              <CanAccess
                isAuthorized={
                  permissions.valve?.commands?.temperatureCorrectionCoef?.read && role === UserRole.INSTALLER
                }
              >
                <VersionGate
                  devices={[
                    {
                      type: "valve",
                      current: valve.softwareVersion,
                      feature: "TEMPERATURE_CORRECTION_COEF",
                    },
                    {
                      type: "gateway",
                      current: valve.gatewaySoftwareVersion,
                      feature: "TEMPERATURE_CORRECTION_COEF",
                    },
                  ]}
                >
                  {({ disabled }) => (
                    // Radix Tooltip.Trigger (asChild) needs a ref-forwarding DOM node; form.AppField isn't one.
                    <div className="w-full">
                      <form.AppField name="temperatureCorrectionCoef">
                        {field => (
                          <field.Slider
                            className="w-full"
                            label={m.calibration()}
                            min={TEMPERATURE_CORRECTION_COEF_LIMITS_MIN}
                            max={TEMPERATURE_CORRECTION_COEF_LIMITS_MAX}
                            step={TEMPERATURE_CORRECTION_COEF_LIMITS_STEP}
                            valueLabelDisplay="auto"
                            disabled={!permissions.valve?.commands?.temperatureCorrectionCoef?.update || disabled}
                          />
                        )}
                      </form.AppField>
                    </div>
                  )}
                </VersionGate>
                {temperatureCorrectionCoef !== undefined && temperatureCorrectionCoef >= 2 && (
                  <Alert severity="warning">{m.valve_edit_calibration_warning()}</Alert>
                )}
                {calibrationOutOfSync && <Alert severity="warning">{m.valve_edit_calibration_sync()}</Alert>}
                <Alert severity="help">{m.calibration_accounts_for_radiator_heat()}</Alert>
              </CanAccess>
            </div>

            <div className="mt-auto flex flex-col items-center gap-[20px]">
              <form.SubmitButton label={m.save()} icon={<PencilIcon />} />
              <CanAccess isAuthorized={permissions.valve?.delete}>
                <DeleteDialog
                  title={m.confirm_delete_valve({ v: valve.mac ?? `${valve.uid}` })}
                  onDelete={() => void handleDeleteValve()}
                >
                  <Button className="text-primary-red">{m.delete()}</Button>
                </DeleteDialog>
              </CanAccess>
            </div>
          </form.AppForm>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
};

EditValveDrawer.displayName = "EditValveDrawer";
